import React, { useMemo } from 'react'
import MUIBreadcrumbs from '@mui/material/Breadcrumbs'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import NavigateNext from '@mui/icons-material/NavigateNext'
import { Link as RouterLink, useLocation, matchRoutes } from 'react-router-dom'
import routesConfig from '../../route'
import { OMRouteConfig } from '../../route/typing'
import useUser from '@context/UserContext/useUser'
import localStore from '@service/local-store'
import useHeaderDetails from '@header/useHeaderDetails'
import { isSurveyCompleted } from '@util/surveyState'
import useBreadcrumb from '@context/BreadCrumbContext/useBreadcrumb'
import useAdobeAnalytics from '@analytics/adobe'
import { useFlags } from 'launchdarkly-react-client-sdk'
import useOrganization from '@admin/OrganizationContext/useOrganization'
import { PROVIDERS } from '../../route.const'

const findRoute = (pathname: string, routes: any) => {
  const matchedRoutes = matchRoutes(routes, pathname)
  return matchedRoutes
    ? matchedRoutes.map(({ route }) => route).filter((route) => route.path)
    : []
}

interface BreadcrumbsProps {
  isLoggedIn: boolean
  hideInitial?: boolean
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  isLoggedIn,
  hideInitial = false,
}) => {
  const location = useLocation()
  const { user, role } = useUser()
  const userType = localStore.get('userType')
  const { breadcrumbs } = useBreadcrumb()
  const { login } = useHeaderDetails()
  const { trackLink } = useAdobeAnalytics()
  const { enableBreadcrumb } = useFlags()
  const { isUnsaved, setWarningPopup } = useOrganization()

  // Initial pages that should not show breadcrumbs
  const initialPages = [
    '/',
    '/providers',
    '/providers/profile',
    '/providers/survey/review',
    '/patients/search-result',
    '/patients/survey/review',
    '/patients/activity',
    '/accesswall',
    '/privacy-policy',
    '/terms-of-use',
  ]

  const handleLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    link: string,
    breadcrumbText: string
  ) => {
    trackLink(
      {
        name: 'BreadCrumb ' + breadcrumbText + ' Click',
        location: link,
        type: 'internal',
      },
      {
        loginStatus: login ? 'logged in' : 'logged out',
        userType: role,
      }
    )
  }

  // Pages that should not show breadcrumbs when survey is not completed and user is a guest user or it's an initial page
  const notShowSurveyBreadcrumbs =
    initialPages.includes(location.pathname) ||
    (location.pathname.includes('/survey') &&
      ((Object.keys(user).length && isSurveyCompleted(user.surveyState)) ||
        userType === 'guestUser'))

  const pathnames = location.pathname.split('/').filter((x) => x)
  const matchedRoutes = useMemo(
    () => findRoute(location.pathname, routesConfig),
    [location.pathname]
  )
  //Disable breadcrumbs for all possible options except Admin
  if (
    (enableBreadcrumb || notShowSurveyBreadcrumbs) &&
    !location.pathname.includes('/admin')
  ) {
    return null
  }
  const validBreadcumbRoutes = matchedRoutes.filter(
    (route: OMRouteConfig) => route.breadcrumbConfig
  )
  const isLast = validBreadcumbRoutes.length

  const isSpecialRoute: boolean =
    location.pathname.includes('providers') ||
    location.pathname.includes('patients')
  let accumulatedPath: string = ''

  const isOrganizationLocationPage =
    location.pathname.includes('/organizations/') &&
    location.pathname.includes('/service-locations/')

  const isClinicianPage =
    location.pathname.includes('/organizations/') &&
    location.pathname.includes('/clinicians/')

  return (
    <Box
      sx={({ ues }) => ({
        ...(isOrganizationLocationPage || isClinicianPage
          ? {
              pt: 3,
              pl: 3,
              mb: 4,
            }
          : {
              py: {
                xs: '12px',
                sm: '12px',
                md: '26px',
                lg: '26px',
                xl: '26px',
              },
              px: {
                xs: '21px',
                sm: '21px',
                md: '42px',
                lg: '84px',
                xl: '84px',
              },
              borderTop: `1px solid ${ues.neutral[10]}`,
            }),
      })}
    >
      <MUIBreadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumbConfig"
      >
        {!hideInitial && (!isLoggedIn || !isSpecialRoute) && (
          <Link
            sx={{
              fontWeight: 'bold',
            }}
            variant={'body3'}
            component={RouterLink}
            to={isLoggedIn && role === PROVIDERS ? `/${role}` : '/'}
            color="inherit"
            onClick={(event) =>
              handleLinkClick(
                event,
                isLoggedIn && role === PROVIDERS ? `/${role}` : '/',
                'Home'
              )
            }
          >
            <Typography
              variant={'body3'}
              color={'primary'}
              sx={{
                fontWeight: 'bold',
                pb: 0.4,
              }}
            >
              Home
            </Typography>
          </Link>
        )}
        {validBreadcumbRoutes.map((route: OMRouteConfig, index: number) => {
          if (route.path === 'service-locations') {
            const orgIndex = pathnames.indexOf('organizations')
            const organizationId =
              orgIndex >= 0 ? pathnames[orgIndex + 1] : null

            if (organizationId) {
              accumulatedPath = `/admin/organizations/${organizationId}/service-locations`
            }
          } else if (route.path === 'clinicians') {
            const orgIndex = pathnames.indexOf('organizations')
            const organizationId =
              orgIndex >= 0 ? pathnames[orgIndex + 1] : null

            if (organizationId) {
              accumulatedPath = `/admin/organizations/${organizationId}/clinicians`
            }
          } else if (route.path === ':organizationId') {
            const orgIndex = pathnames.indexOf('organizations')
            const organizationId =
              orgIndex >= 0 ? pathnames[orgIndex + 1] : null

            if (organizationId) {
              accumulatedPath = `/admin/organizations/${organizationId}`
            }
          } else {
            accumulatedPath += `/${pathnames[index]}`
          }

          if (!route.breadcrumbConfig || !route.breadcrumbConfig?.primaryText) {
            return null
          }

          // Set the breadcrumb text for the search result page
          let breadcrumbText =
            isLoggedIn && route.breadcrumbConfig.secondaryText
              ? route.breadcrumbConfig.secondaryText
              : route.breadcrumbConfig.primaryText

          if (route.path === 'admin') return null

          // Set the breadcrumb text for the section and question pages
          if (route.path === ':sectionId') {
            breadcrumbText = breadcrumbs?.sectionName || ''
          } else if (route.path === ':questionId') {
            breadcrumbText = breadcrumbs.questionName || ''
          } else if (route.path === 'provider-profile/:id') {
            breadcrumbText = breadcrumbs.profileName || ''
          } else if (route.path === ':organizationId') {
            breadcrumbText = breadcrumbs.organizationName || ''
          } else if (route.path === 'service-locations') {
            breadcrumbText = breadcrumbs.serviceLocations || ''
          } else if (route.path === ':locationId') {
            breadcrumbText = breadcrumbs.serviceLocationName || ''
          } else if (route.path === ':clinicianId') {
            breadcrumbText = breadcrumbs.cliniciansName || ''
          }

          /*if (login && location.pathname?.includes('providers/privacy')) {
            breadcrumbText = 'Welcome' || ''
          }

          if (login && location.pathname === '/patients/expectation') {
            if (breadcrumbText.toLowerCase() === 'my matches')
              breadcrumbText = 'Welcome' || ''
          }*/

          // If the route is the last one, render the breadcrumb text as a Typography component
          return isLast === index + 1 ? (
            <Typography
              variant={'body3'}
              color={'text.secondary'}
              key={route.path}
            >
              {breadcrumbText}
            </Typography>
          ) : (
            <Link
              key={accumulatedPath}
              component={RouterLink}
              to={!isUnsaved ? accumulatedPath : ''}
              onClick={(event) =>
                isUnsaved
                  ? setWarningPopup(true)
                  : handleLinkClick(event, accumulatedPath, breadcrumbText)
              }
            >
              <Typography
                variant={'body3'}
                color={'primary'}
                sx={{
                  fontWeight: 'bold',
                  pb: 0.4,
                }}
              >
                {breadcrumbText}
              </Typography>
            </Link>
          )
        })}
      </MUIBreadcrumbs>
    </Box>
  )
}

export default Breadcrumbs
